import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

const POLL_MS = 45000;

function getHeaders() {
  const t = sessionStorage.getItem('sessionToken');
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
  };
}

function sortedStatusKeys(record) {
  const keys = Object.keys(record || {});
  return keys.sort((a, b) => {
    const na = parseInt(a, 10);
    const nb = parseInt(b, 10);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    return a.localeCompare(b);
  });
}

function sumTotals(totalsByStatus) {
  return Object.values(totalsByStatus || {}).reduce((s, n) => s + (Number(n) || 0), 0);
}

function sumErrors(totalsByStatus) {
  return Object.entries(totalsByStatus || {}).reduce((s, [code, n]) => {
    const c = parseInt(code, 10);
    if (!Number.isNaN(c) && c >= 400) return s + (Number(n) || 0);
    return s;
  }, 0);
}

function defaultFromLocal() {
  const d = new Date(Date.now() - 24 * 60 * 60 * 1000);
  return toLocalInput(d);
}

function defaultToLocal() {
  return toLocalInput(new Date());
}

function toLocalInput(d) {
  const pad = (x) => String(x).padStart(2, '0');
  const y = d.getFullYear();
  const m = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const h = pad(d.getHours());
  const min = pad(d.getMinutes());
  return `${y}-${m}-${day}T${h}:${min}`;
}

function localInputToIso(local) {
  if (!local) return null;
  const t = new Date(local).getTime();
  if (Number.isNaN(t)) return null;
  return new Date(t).toISOString();
}

const SORT_ENDPOINT = 'endpoint';
const SORT_TOTAL = 'total';
const SORT_ERRORS = 'errorCount';
const statusSortKey = (code) => `status:${code}`;

function compareEndpoints(a, b) {
  return a.endpoint.localeCompare(b.endpoint, undefined, { sensitivity: 'base' });
}

function ApiResponseStats() {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fromLocal, setFromLocal] = useState(defaultFromLocal);
  const [toLocal, setToLocal] = useState(defaultToLocal);
  const [filterText, setFilterText] = useState('');
  const [sortBy, setSortBy] = useState(SORT_ERRORS);
  const [sortDir, setSortDir] = useState('desc');

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      const fromIso = localInputToIso(fromLocal);
      const toIso = localInputToIso(toLocal);
      if (fromIso) params.set('from', fromIso);
      if (toIso) params.set('to', toIso);
      if (filterText.trim()) params.set('filter', filterText.trim());

      const res = await fetch(`${BASE_URL}/admin/api-response-stats/summary?${params}`, {
        credentials: 'include',
        headers: getHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      if (json.statusCode !== '10000') {
        setError(json.message || `Request failed (${res.status})`);
        setPayload(null);
        return;
      }
      setPayload(json.data);
    } catch (e) {
      setError(e.message || 'Request failed');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [fromLocal, toLocal, filterText]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    const id = setInterval(() => fetchSummary(), POLL_MS);
    return () => clearInterval(id);
  }, [fetchSummary]);

  const statusCols = useMemo(
    () => sortedStatusKeys(payload?.totalsByStatus),
    [payload?.totalsByStatus],
  );

  const totalReq = useMemo(() => sumTotals(payload?.totalsByStatus), [payload?.totalsByStatus]);
  const totalErr = useMemo(() => sumErrors(payload?.totalsByStatus), [payload?.totalsByStatus]);

  const statusBarRows = useMemo(() => {
    const t = payload?.totalsByStatus || {};
    const entries = Object.entries(t).map(([code, count]) => ({
      code,
      count: Number(count) || 0,
    }));
    entries.sort((a, b) => b.count - a.count);
    const max = Math.max(...entries.map((e) => e.count), 1);
    return entries.map((e) => ({ ...e, pct: (e.count / max) * 100 }));
  }, [payload?.totalsByStatus]);

  const sortedEndpoints = useMemo(() => {
    const rows = [...(payload?.endpoints || [])];
    rows.sort((ra, rb) => {
      let cmp = 0;
      if (sortBy === SORT_ENDPOINT) {
        cmp = compareEndpoints(ra, rb);
      } else if (sortBy === SORT_TOTAL) {
        cmp = ra.total - rb.total;
      } else if (sortBy === SORT_ERRORS) {
        cmp = ra.errorCount - rb.errorCount;
      } else if (sortBy.startsWith('status:')) {
        const code = sortBy.slice('status:'.length);
        const va = ra.byStatus?.[code] ?? 0;
        const vb = rb.byStatus?.[code] ?? 0;
        cmp = va - vb;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [payload?.endpoints, sortBy, sortDir]);

  const requestSort = (column, isNumeric) => {
    if (sortBy === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir(isNumeric ? 'desc' : 'asc');
    }
  };

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-8">
                <h3>API response stats</h3>
                <Typography variant="body2" color="text.secondary">
                  HTTP status counts per route (UTC hourly buckets). Companion:{' '}
                  <Link to="/admin/api-latency">API Latency</Link>.
                </Typography>
              </div>
            </div>
          </div>
        </div>
        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <Card>
                <CardContent>
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                      {error}
                    </Alert>
                  )}

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                        <Typography variant="h4" fontWeight={700}>
                          {payload ? totalReq.toLocaleString() : '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total requests
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          bgcolor: totalErr > 0 ? '#ffebee' : '#e8f5e9',
                        }}
                      >
                        <Typography
                          variant="h4"
                          fontWeight={700}
                          color={totalErr > 0 ? 'error.main' : 'success.main'}
                        >
                          {payload ? totalErr.toLocaleString() : '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Errors (status ≥ 400)
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                        <Typography variant="h4" fontWeight={700}>
                          {payload != null ? payload.endpointCount : '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Distinct routes (window)
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                        <Typography variant="h4" fontWeight={700}>
                          {payload != null ? payload.hoursScanned : '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          UTC hours scanned
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  {payload && statusBarRows.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Status mix (global)
                      </Typography>
                      <Grid container spacing={1}>
                        {statusBarRows.map(({ code, count, pct }) => (
                          <Grid item xs={12} key={code}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{ width: 40, fontFamily: 'monospace', fontWeight: 600 }}
                              >
                                {code}
                              </Typography>
                              <Box sx={{ flex: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={pct}
                                  sx={{
                                    height: 8,
                                    borderRadius: 1,
                                    bgcolor: '#eee',
                                    '& .MuiLinearProgress-bar': {
                                      bgcolor:
                                        parseInt(code, 10) >= 500
                                          ? '#c62828'
                                          : parseInt(code, 10) >= 400
                                            ? '#ef6c00'
                                            : '#2e7d32',
                                    },
                                  }}
                                />
                              </Box>
                              <Typography variant="caption" sx={{ minWidth: 72, textAlign: 'right' }}>
                                {count.toLocaleString()}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                      label="From (local → ISO UTC)"
                      size="small"
                      type="datetime-local"
                      value={fromLocal}
                      onChange={(e) => setFromLocal(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 240 }}
                    />
                    <TextField
                      label="To (local → ISO UTC)"
                      size="small"
                      type="datetime-local"
                      value={toLocal}
                      onChange={(e) => setToLocal(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 240 }}
                    />
                    <TextField
                      label="Route filter"
                      size="small"
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                      placeholder="e.g. matchsc, GET /admin"
                      sx={{ width: 220 }}
                    />
                    <Button variant="contained" size="small" onClick={fetchSummary} disabled={loading}>
                      {loading ? <CircularProgress size={18} /> : 'Apply'}
                    </Button>
                  </Box>

                  {payload && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      Effective range: {payload.from} → {payload.to}
                      {payload.truncated && (
                        <>
                          <br />
                          {payload.filters?.limit != null
                            ? `Showing at most ${payload.filters.limit} rows (server cap). Narrow the route filter if needed.`
                            : 'Response list was truncated by the server. Narrow the route filter if needed.'}
                        </>
                      )}
                      {payload.note && (
                        <>
                          <br />
                          {payload.note}
                        </>
                      )}
                      <br />
                      Counts are UTC hourly; very short windows still use whole hours touched. Auto-refresh
                      every {POLL_MS / 1000}s.
                    </Typography>
                  )}

                  {loading && !payload ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#fafafa' }}>
                            <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>
                              <TableSortLabel
                                active={sortBy === SORT_ENDPOINT}
                                direction={sortBy === SORT_ENDPOINT ? sortDir : 'asc'}
                                onClick={() => requestSort(SORT_ENDPOINT, false)}
                              >
                                Endpoint
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active={sortBy === SORT_TOTAL}
                                direction={sortBy === SORT_TOTAL ? sortDir : 'desc'}
                                onClick={() => requestSort(SORT_TOTAL, true)}
                                sx={{ justifyContent: 'flex-end' }}
                              >
                                Total
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active={sortBy === SORT_ERRORS}
                                direction={sortBy === SORT_ERRORS ? sortDir : 'desc'}
                                onClick={() => requestSort(SORT_ERRORS, true)}
                                sx={{ justifyContent: 'flex-end' }}
                              >
                                Errors
                              </TableSortLabel>
                            </TableCell>
                            {statusCols.map((code) => {
                              const sk = statusSortKey(code);
                              return (
                                <TableCell key={code} align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                  <TableSortLabel
                                    active={sortBy === sk}
                                    direction={sortBy === sk ? sortDir : 'desc'}
                                    onClick={() => requestSort(sk, true)}
                                    sx={{ justifyContent: 'flex-end' }}
                                  >
                                    {code}
                                  </TableSortLabel>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {!payload?.endpoints?.length ? (
                            <TableRow>
                              <TableCell colSpan={3 + statusCols.length} align="center">
                                <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                  No rows for this range or filter.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            sortedEndpoints.map((row) => (
                              <TableRow key={row.endpoint}>
                                <TableCell>
                                  <Tooltip title={row.endpoint} arrow>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.8rem',
                                        maxWidth: 420,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {row.endpoint}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="right">{row.total.toLocaleString()}</TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    fontWeight: row.errorCount > 0 ? 600 : 400,
                                    color: row.errorCount > 0 ? 'error.main' : 'inherit',
                                  }}
                                >
                                  {row.errorCount.toLocaleString()}
                                </TableCell>
                                {statusCols.map((code) => (
                                  <TableCell key={code} align="right">
                                    {(row.byStatus?.[code] ?? 0).toLocaleString()}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Component>
  );
}

export default ApiResponseStats;
